import { NgClass } from '@angular/common'
import { Component, DestroyRef, input, OnChanges, OnInit, output, signal, SimpleChanges, WritableSignal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import * as DCTSetupTypes from '@app/modules/events/types/dct-setup.type'
import { DCTSettings } from '@app/modules/personas/interfaces/dct-settings.interface'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { Subject, takeUntil } from 'rxjs'

@Component({
  selector: 'ngx-dct-product',
  templateUrl: './dct-product.component.html',
  styleUrls: ['./dct-product.component.scss'],
  imports: [ReactiveFormsModule, NgxTippyModule, NgClass]
})
export class DCTProductComponent implements OnInit, OnChanges {
  public persona = input.required<Persona>()
  public dctSettings = input.required<DCTSetupTypes.DCTSetup>()
  public isConnected = input<boolean>(false)
  public productDeleted = output<void>()
  public productConnected = output<DCTSettings>()
  public personaSelected = output<number>()
  public identifier = signal<string>(crypto.randomUUID())
  public form: FormGroup
  public sessions: WritableSignal<DCTSetupTypes.DCTSession[]> = signal([])
  public sessionSlots: WritableSignal<DCTSetupTypes.DCTSessionSlot[]> = signal([])
  public products: WritableSignal<DCTSetupTypes.DCTProduct[]> = signal([])
  public productVariants: WritableSignal<DCTSetupTypes.DCTProductVariant[]> = signal([])
  public sessionName = signal<string>('')
  public sessionSlotName = signal<string>('')
  public productName = signal<string>('')
  public productVariantName = signal<string>('')
  private _formReset$ = new Subject<void>()

  constructor(private _fb: FormBuilder, private _destroyRef: DestroyRef) {}

  ngOnInit() {
    this._formReset$.pipe(takeUntilDestroyed(this._destroyRef))
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isConnected'] && !changes['isConnected'].firstChange) this._formReset$.next()
    this._initializeForm()
  }

  /**
   * Initialize the form
   */
  private _initializeForm() {
    this.form = this._fb.group({
      session: this._fb.control({ value: '', disabled: false }, Validators.required),
      sessionSlot: this._fb.control({ value: '', disabled: true }, Validators.required),
      product: this._fb.control({ value: '', disabled: true }, Validators.required),
      productVariant: this._fb.control({ value: '', disabled: true }, Validators.required),
      price: this._fb.control({ value: 0, disabled: true }, Validators.required),
      triggerOn: this._fb.control({ value: 'OnAttended', disabled: true }, Validators.required)
    })
    this.sessions.set(this.dctSettings().sessions ?? [])
    this._handleFormValueChanges()
    this._populateFormValues()
  }

  /**
   * Handle the form value changes to update the dependent controls
   */
  private _handleFormValueChanges() {
    const controlDependencies = new Map<string, string>([
      ['sessionSlot', 'session'],
      ['product', 'sessionSlot'],
      ['productVariant', 'product'],
      ['price', 'productVariant']
    ])
    controlDependencies.forEach((dependency, dependant) => {
      this.form
        .get(dependency)
        .valueChanges.pipe(takeUntil(this._formReset$), takeUntilDestroyed(this._destroyRef))
        .subscribe((value) => {
          switch (dependency) {
            case 'session':
              const session = this.sessions().find((session) => session.code === value)
              this.sessionSlots.set(session?.sessionSlots ?? [])
              this.sessionName.set(session?.name ?? '')
              this.products.set([])
              this.productVariants.set([])
              break
            case 'sessionSlot':
              const sessionSlot = this.sessionSlots().find((sessionSlot) => sessionSlot.code === value)
              this.products.set(sessionSlot?.products ?? [])
              this.sessionSlotName.set(sessionSlot?.name ?? '')
              this.productVariants.set([])
              break
            case 'product':
              const product = this.products().find((product) => product.code === value)
              this.productVariants.set(product?.variants ?? [])
              this.productName.set(product?.name ?? '')
              break
            case 'productVariant':
              const productVariant = this.productVariants().find((productVariant) => productVariant.code === value)
              this.form.get('price').setValue(productVariant?.price ?? 0)
              if (this.isConnected()) this.form.get(dependency).disable({ emitEvent: false })
              this.productVariantName.set(productVariant?.name ?? '')
              return
          }
          this.form.get(dependant).setValue('')
          if (!value) {
            this.form.get(dependant).disable()
            return
          }
          this.form.get(dependant).enable({ emitEvent: false })
          if (this.isConnected()) this.form.get(dependency).disable({ emitEvent: false })
        })
    })
  }

  /**
   * Populate the form values
   */
  private _populateFormValues() {
    if (!this.isConnected() || !this.persona()?.options?.dct) return
    this.form.patchValue({
      session: this.persona().options.dct.session,
      sessionSlot: this.persona().options.dct.sessionSlot,
      product: this.persona().options.dct.product,
      productVariant: this.persona().options.dct.productVariant,
      price: this.persona().options.dct.price,
      triggerOn: this.persona().options.dct.triggerOn
    })
    this.form.markAsPristine()
  }

  /**
   * Connect the product to the persona
   */
  public connect() {
    this.productConnected.emit(this.form.getRawValue())
  }

  /**
   * Disconnect the product from the persona
   */
  public disconnect() {
    this.productDeleted.emit()
  }
}
